import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Upload,
  Refresh,
  Delete,
  Visibility,
  Edit,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@config/api';
import { formatDateOnly } from '@utils/dateFormatter';

interface RomanianAddress {
  id: string;
  cod_postal: string;
  tip_artera: string;
  denumire_artera: string;
  numar: string;
  judet: string;
  oras: string;
  localitate: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const RomanianData: React.FC = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<RomanianAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<any>(null);
  
  // State pentru selectare multipla
  const [selectedAddresses, setSelectedAddresses] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // State pentru dialog-uri
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<RomanianAddress | null>(null);
  
  // State pentru notificari
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Functie pentru incarcarea datelor
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Incarca statisticile
      const statsResponse = await fetch(`${API_BASE_URL}/admin/romanian-data/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.data);
      }

      // Incarca primele 100 de adrese pentru afisare
      const response = await fetch(`${API_BASE_URL}/admin/romanian-data/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          limit: 100,
          offset: 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAddresses(data.data || []);
      } else {
        setError('Eroare la incarcarea datelor');
      }
    } catch (err) {
      setError('Eroare de conexiune');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Functie pentru cautare
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadData();
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/romanian-data/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          search: searchTerm,
          limit: 100,
          offset: 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAddresses(data.data || []);
      }
    } catch (err) {
      setError('Eroare la cautare');
    } finally {
      setLoading(false);
    }
  };

  // Functie pentru golirea tabelei
  const handleClearTable = async () => {
    setDeleteDialogOpen(true);
  };

  // Functie pentru confirmarea stergerii totale
  const handleConfirmClearTable = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/romanian-data/clear-addresses`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setAddresses([]);
        setSelectedAddresses([]);
        setSelectAll(false);
        loadData(); // Reincarca statisticile
        setSnackbar({
          open: true,
          message: 'Toate adresele au fost sterse cu succes!',
          severity: 'success'
        });
      } else {
        setError('Eroare la stergerea datelor');
      }
    } catch (err) {
      setError('Eroare de conexiune');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Functii pentru selectare multipla
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedAddresses(addresses.map(addr => addr.id));
      setSelectAll(true);
    } else {
      setSelectedAddresses([]);
      setSelectAll(false);
    }
  };

  const handleSelectAddress = (addressId: string) => {
    setSelectedAddresses(prev => {
      if (prev.includes(addressId)) {
        const newSelected = prev.filter(id => id !== addressId);
        setSelectAll(newSelected.length === addresses.length);
        return newSelected;
      } else {
        const newSelected = [...prev, addressId];
        setSelectAll(newSelected.length === addresses.length);
        return newSelected;
      }
    });
  };

  // Functie pentru stergerea adreselor selectate
  const handleDeleteSelected = async () => {
    if (selectedAddresses.length === 0) return;

    try {
      // Sterge adresele selectate una cate una
      for (const addressId of selectedAddresses) {
        await fetch(`${API_BASE_URL}/admin/romanian-data/addresses/${addressId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      // Actualizeaza lista locala
      setAddresses(prev => prev.filter(addr => !selectedAddresses.includes(addr.id)));
      setSelectedAddresses([]);
      setSelectAll(false);
      
      setSnackbar({
        open: true,
        message: `${selectedAddresses.length} adrese au fost sterse cu succes!`,
        severity: 'success'
      });
    } catch (err) {
      setError('Eroare la stergerea adreselor selectate');
    }
  };

  // Functii pentru vizualizare si editare
  const handleViewAddress = (address: RomanianAddress) => {
    setSelectedAddress(address);
    setViewDialogOpen(true);
  };

  const handleEditAddress = (address: RomanianAddress) => {
    setSelectedAddress(address);
    setEditDialogOpen(true);
  };

  // Functie pentru salvarea modificarilor
  const handleSaveAddress = async (updatedAddress: Partial<RomanianAddress>) => {
    if (!selectedAddress) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/romanian-data/addresses/${selectedAddress.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updatedAddress)
      });

      if (response.ok) {
        // Actualizeaza lista locala
        setAddresses(prev => prev.map(addr => 
          addr.id === selectedAddress.id 
            ? { ...addr, ...updatedAddress, updated_at: new Date().toISOString() }
            : addr
        ));
        
        setSnackbar({
          open: true,
          message: 'Adresa a fost actualizata cu succes!',
          severity: 'success'
        });
        setEditDialogOpen(false);
      } else {
        setError('Eroare la actualizarea adresei');
      }
    } catch (err) {
      setError('Eroare de conexiune');
    }
  };

  // Functie pentru inchiderea notificarilor
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        📊 Date Romanesti - Adrese
      </Typography>

      {/* Statistici */}
      {stats && (
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 Statistici
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${stats.addresses?.total || 0} adrese totale`} 
                  color="primary" 
                  variant="outlined" 
                />
                <Chip 
                  label={`${stats.addresses?.judete || 0} judete`} 
                  color="secondary" 
                  variant="outlined" 
                />
                <Chip 
                  label={`${stats.addresses?.localitati || 0} localitati`} 
                  color="info" 
                  variant="outlined" 
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Actiuni */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<Upload />}
          onClick={() => navigate('/admin/import')}
        >
          Import Excel
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
          disabled={loading}
        >
          Reincarca
        </Button>

        {selectedAddresses.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteSelected}
          >
            Sterge {selectedAddresses.length} selectate
          </Button>
        )}

        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={handleClearTable}
        >
          Sterge toate
        </Button>
      </Box>

      {/* Cautare */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔍 Cautare adrese
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                placeholder="Cauta dupa cod postal, judet, localitate..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearchTerm('')}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
              >
                Cauta
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Tabel cu date */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 Adrese Romanesti
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : addresses.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nu exista adrese in baza de date
              </Typography>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={() => navigate('/admin/import')}
                sx={{ mt: 2 }}
              >
                Importeaza date
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAll}
                        indeterminate={selectedAddresses.length > 0 && selectedAddresses.length < addresses.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Cod Postal</TableCell>
                    <TableCell>Tip Artera</TableCell>
                    <TableCell>Denumire Artera</TableCell>
                    <TableCell>Numar</TableCell>
                    <TableCell>Judet</TableCell>
                    <TableCell>Localitate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addresses.map((address) => (
                    <TableRow key={address.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAddresses.includes(address.id)}
                          onChange={() => handleSelectAddress(address.id)}
                        />
                      </TableCell>
                      <TableCell>{address.cod_postal}</TableCell>
                      <TableCell>{address.tip_artera}</TableCell>
                      <TableCell>{address.denumire_artera}</TableCell>
                      <TableCell>{address.numar}</TableCell>
                      <TableCell>{address.judet}</TableCell>
                      <TableCell>{address.localitate}</TableCell>
                      <TableCell>
                        <Chip
                          label={address.is_active ? 'Activ' : 'Inactiv'}
                          color={address.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Vezi detalii">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewAddress(address)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editeaza">
                            <IconButton 
                              size="small"
                              onClick={() => handleEditAddress(address)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog confirmare stergere totala */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>⚠️ Confirmare stergere totala</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            <strong>ATENTIE!</strong> Aceasta actiune va sterge TOATE adresele din baza de date!
          </Typography>
          <Typography variant="body2">
            • {stats?.addresses?.total || 0} adrese vor fi sterse definitiv
          </Typography>
          <Typography variant="body2">
            • Aceasta actiune nu poate fi anulata
          </Typography>
          <Typography variant="body2">
            • Va trebui sa reimportati datele daca doriti sa le recuperati
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Anuleaza
          </Button>
          <Button 
            onClick={handleConfirmClearTable} 
            color="error" 
            variant="contained"
          >
            Sterge toate adresele
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog vizualizare adresa */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>👁️ Detalii adresa</DialogTitle>
        <DialogContent>
          {selectedAddress && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Cod Postal</Typography>
                <Typography variant="body1">{selectedAddress.cod_postal}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Tip Artera</Typography>
                <Typography variant="body1">{selectedAddress.tip_artera}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Denumire Artera</Typography>
                <Typography variant="body1">{selectedAddress.denumire_artera}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Numar</Typography>
                <Typography variant="body1">{selectedAddress.numar}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Judet</Typography>
                <Typography variant="body1">{selectedAddress.judet}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Localitate</Typography>
                <Typography variant="body1">{selectedAddress.localitate}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedAddress.is_active ? 'Activ' : 'Inactiv'}
                  color={selectedAddress.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Data crearii</Typography>
                <Typography variant="body1">
                  {formatDateOnly(selectedAddress.created_at)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Inchide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog editare adresa */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>✏️ Editeaza adresa</DialogTitle>
        <DialogContent>
          {selectedAddress && (
            <AddressEditForm
              address={selectedAddress}
              onSave={handleSaveAddress}
              onCancel={() => setEditDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Notificari */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Componenta pentru editarea adresei
interface AddressEditFormProps {
  address: RomanianAddress;
  onSave: (updatedAddress: Partial<RomanianAddress>) => void;
  onCancel: () => void;
}

const AddressEditForm: React.FC<AddressEditFormProps> = ({ address, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    tip_artera: address.tip_artera || '',
    denumire_artera: address.denumire_artera || '',
    numar: address.numar || '',
    judet: address.judet || '',
    localitate: address.localitate || '',
    is_active: address.is_active
  });

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
      <TextField
        fullWidth
        label="Tip Artera"
        value={formData.tip_artera}
        onChange={(e) => handleChange('tip_artera', e.target.value)}
      />
      <TextField
        fullWidth
        label="Denumire Artera"
        value={formData.denumire_artera}
        onChange={(e) => handleChange('denumire_artera', e.target.value)}
      />
      <TextField
        fullWidth
        label="Numar"
        value={formData.numar}
        onChange={(e) => handleChange('numar', e.target.value)}
      />
      <TextField
        fullWidth
        label="Judet"
        value={formData.judet}
        onChange={(e) => handleChange('judet', e.target.value)}
      />
      <TextField
        fullWidth
        label="Localitate"
        value={formData.localitate}
        onChange={(e) => handleChange('localitate', e.target.value)}
      />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
            />
          }
          label="Activ"
        />
      </Box>
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={onCancel}>
          Anuleaza
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Salveaza
        </Button>
      </Box>
    </Box>
  );
};

export default RomanianData;
